import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Returns whether the currently logged-in user is flagged as an internal
 * doctor/specialist on the linked staff record.
 *
 * Backed by `staff.is_doctor` (added by migration
 * `20260521150000_staff_is_doctor_flag.sql`). Falls back to a substring
 * match on `staff.role` for the transition period before the flag is
 * populated for each doctor.
 *
 * Used to gate the doctor-private `/my-patients` route and its sidebar
 * entry. Resolves Twin City fixes-backlog item #23.
 */
export const useIsDoctor = (): { isDoctor: boolean; isLoading: boolean; staffId: string | null } => {
  const { user, getUserStaffId } = useAuth();
  const staffId = getUserStaffId() ?? null;
  const [isDoctor, setIsDoctor] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const checkDoctorStatus = async () => {
      if (!user || !staffId) {
        if (!cancelled) {
          setIsDoctor(false);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      // The new `is_doctor` column may not be in the generated types yet,
      // so we cast through `unknown` to keep TypeScript clean while still
      // pulling the column at runtime.
      const { data, error } = await (supabase
        .from('staff')
        .select('role, is_doctor')
        .eq('id', staffId)
        .single() as unknown as Promise<{
          data: { role: string | null; is_doctor: boolean | null } | null;
          error: unknown;
        }>);

      if (cancelled) return;

      if (error || !data) {
        setIsDoctor(false);
        setIsLoading(false);
        return;
      }

      const flagged = data.is_doctor === true;
      const roleLooksLikeDoctor =
        typeof data.role === 'string' &&
        /\b(doctor|specialist|endodontist|dentist)\b/i.test(data.role);

      setIsDoctor(flagged || roleLooksLikeDoctor);
      setIsLoading(false);
    };

    void checkDoctorStatus();

    return () => {
      cancelled = true;
    };
  }, [user, staffId]);

  return { isDoctor, isLoading, staffId };
};
